import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  extractBackendErrorCode,
  isRecoverableError,
} from "../constants/backend-error-messages";
import { ERROR_MESSAGES } from "../constants/error-messages";
import { IErrorResponse } from "../interfaces/all";
import { ErrorHandler } from "../utils/error-handler";
import { useToast } from "./useToast";

export const useApiError = () => {
  const navigate = useNavigate();
  const { error: showErrorToast, success: showSuccessToast } = useToast();

  const handleError = useCallback(
    (
      error: unknown,
      options?: {
        showToast?: boolean;
        fallbackMessage?: string;
        onError?: (errorResponse: IErrorResponse) => void;
      },
    ) => {
      const { showToast = true, fallbackMessage, onError } = options || {};

      const errorResponse = ErrorHandler.handleApiError(error);

      // Executar callback personalizado se fornecido
      if (onError) {
        onError(errorResponse);
      }

      // Determinar mensagem final
      let finalMessage =
        errorResponse.userMessage || ERROR_MESSAGES[errorResponse.type];
      if (fallbackMessage) {
        finalMessage = fallbackMessage;
      }

      // Mostrar toast se habilitado
      if (showToast) {
        showErrorToast(finalMessage);
      }

      // Ações específicas baseadas no tipo de erro
      if (errorResponse.type === "authentication") {
        navigate("/login");
      }

      return errorResponse;
    },
    [navigate, showErrorToast],
  );

  const handleSuccess = useCallback(
    (
      message: string,
      options?: {
        duration?: number;
        showToast?: boolean;
      },
    ) => {
      const { showToast = true } = options || {};

      if (showToast) {
        showSuccessToast(message);
      }
    },
    [showSuccessToast],
  );

  const handleApiCall = useCallback(
    async <T>(
      apiCall: () => Promise<T>,
      options?: {
        onSuccess?: (data: T) => void;
        onError?: (error: IErrorResponse) => void;
        successMessage?: string;
        showSuccessToast?: boolean;
        showErrorToast?: boolean;
        fallbackErrorMessage?: string;
      },
    ): Promise<T | null> => {
      try {
        const data = await apiCall();

        // Executar callback de sucesso
        if (options?.onSuccess) {
          options.onSuccess(data);
        }

        // Mostrar mensagem de sucesso
        if (options?.successMessage && options?.showSuccessToast !== false) {
          handleSuccess(options.successMessage, { showToast: true });
        }

        return data;
      } catch (error) {
        handleError(error, {
          showToast: options?.showErrorToast !== false,
          fallbackMessage: options?.fallbackErrorMessage,
          onError: options?.onError,
        });

        return null;
      }
    },
    [handleError, handleSuccess],
  );

  const isErrorRecoverable = useCallback((error: unknown): boolean => {
    const errorResponse = ErrorHandler.handleApiError(error);
    const backendCode = extractBackendErrorCode(errorResponse.message);

    if (backendCode) {
      return isRecoverableError(backendCode);
    }

    // Erros de rede são geralmente recuperáveis
    if (
      errorResponse.type === "internal" &&
      errorResponse.code === "NET-0001"
    ) {
      return true;
    }

    // Erros de autenticação não são recuperáveis
    if (errorResponse.type === "authentication") {
      return false;
    }

    // Outros erros são considerados recuperáveis por padrão
    return true;
  }, []);

  return {
    handleError,
    handleSuccess,
    handleApiCall,
    isErrorRecoverable,
  };
};
